import React, { useCallback, useState } from "react";

import AssignmentEditor from "./AssignmentEditor";
import { ViewState } from "./EditCoachingPlanHead";
import { classNames } from "utility/Utils";
import { getValueAtPath } from "utility/ObjectUtils";
import { scoreBadgeBackgroundColor, scoreBarColor, scoreEncouragement, scoreTextColor } from "./ScoreUtils";
import { getCoachingPlanWeekBadgeImagePath } from "./BadgeUtils";

interface PersonalRoutineEditorProps {
    activityStats: any;
    constRoutine?: any;
    firstName: String;
    personalRoutine?: any;
    routineBadgeData?: any;
    selectedWeek?: any;
    state: ViewState;
    vetKitVersion?: string;
    // Callbacks
    onTogglePersonalRoutine?: any;
    setActivityCallback?: any;
    setDurationCallback?: any;
    setOptionsCallback?: any;
}

const PersonalRoutineEditor = ({
    activityStats,
    constRoutine,
    firstName,
    personalRoutine,
    routineBadgeData,
    selectedWeek,
    state,
    vetKitVersion,
    // Callbacks
    onTogglePersonalRoutine,
    setActivityCallback,
    setDurationCallback,
    setOptionsCallback
}: PersonalRoutineEditorProps) => {

    const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

    // ---=== useCallbacks ===--- //

    const refreshCallback = useCallback(() => {
        setRefreshTrigger(prev => ++prev);
    }, []);

    // ---=== Utilities ===--- //

    const hasDuration = (val) => {
        if (val && typeof (val) !== "string") {
            let duration: number = getValueAtPath(activityStats[val.Id], "Settings.Duration.Value", 120);
            return getValueAtPath(val, "Settings.Duration", duration);
        }
        return 0;
    };

    // ---=== Render ===--- //

    return (
        <div className="text-wrap w-full h- px-6 py-[15px] flex-col justify-start items-start gap-2.5 inline-flex">
            <div className="self-stretch  justify-start items-center gap-6 inline-flex">
                <div className="text-gray-900 text-xl font-semibold leading-[30px]">
                    {firstName ? `${firstName.split(" ")[0]}'s Routine` : ""}
                </div>
                {
                    state && (state === ViewState.Active || state === ViewState.Completed || state === ViewState.Interrupted || state === ViewState.CompletedPost) &&
                    <img className={"w-9 h-9"} src={getCoachingPlanWeekBadgeImagePath(routineBadgeData?.[selectedWeek]?.count > 0 ? routineBadgeData[selectedWeek].count : 0, selectedWeek)} alt="icon"/>
                }
                {
                    state && (state === ViewState.Active || state === ViewState.Completed || state === ViewState.Interrupted || state === ViewState.CompletedPost) && (
                        <div className="w-28 h-[22px] mix-blend-multiply justify-start items-start inline-flex">
                            <div className="pl-1.5 pr-2 py-0.5 bg-gray-50 rounded-2xl justify-center items-center gap-1 flex">
                                <img className="w-3 h-3 relative" src="/icons/check-circle-fancy.svg" alt="icon"/>
                                <div className="text-center text-blue-800 text-xs font-medium leading-[18px]">
                                    {
                                        routineBadgeData && routineBadgeData[selectedWeek] !== undefined
                                            ? routineBadgeData[selectedWeek].count + (routineBadgeData[selectedWeek].count !== 1 ? " completions" : " completion")
                                            : "0 completions"
                                    }
                                </div>
                            </div>
                        </div>
                    )
                }
                {
                    state && (state === ViewState.Active) && personalRoutine && (
                        <div className="grow shrink w-[100%] basis-0 h-full items-center gap-2 flex">
                            <div className="justify-center items-center flex">
                                <input
                                    className="w-5 h-5 p-0.5 bg-gray-50 rounded border border-blue-600 justify-center items-center flex cursor-pointer"
                                    type="checkbox"
                                    checked={personalRoutine ? personalRoutine.enabled : false}
                                    onChange={onTogglePersonalRoutine}
                                />
                            </div>
                            <div className="text-gray-600 text-normal grow shrink w-[100%] leading-tight whitespace-nowrap">Enable in VR</div>
                        </div>
                    )
                }
            </div>
            {
                state && state === ViewState.Pending && (
                    <div className="w-full justify-start items-start gap-6 inline-flex">
                        <div className="w-full text-wrap text-gray-900 text-sm font-normal leading-tight">Personal Routines must be edited in the current plan week.</div>
                    </div>
                )
            }
            {
                state && state === ViewState.Cancelled && (
                    <div className="w-full justify-start items-start gap-6 inline-flex">
                        <div className="w-full text-wrap text-gray-900 text-sm font-normal leading-tight"> No recorded routine data for this week.</div>
                    </div>
                )
            }
            {
                state && (state === ViewState.Active || state === ViewState.Completed || state === ViewState.Interrupted || state === ViewState.CompletedPost) && (
                    <div className="w-full justify-start items-start gap-6">
                        <div className="w-full text-wrap text-gray-900 text-sm font-normal leading-tight">
                            {" "}
                            A sequence of movement activities for restoring functional capability, tailored to each patient’s individual needs. A Karuna Rating is awarded on completion, representing the patient’s level of function and range of motion. The patient may complete their
                            routine across multiple sessions.
                        </div>
                        {
                            (routineBadgeData?.[selectedWeek] && routineBadgeData[selectedWeek].rating.score !== 0)
                                ? (
                                    <div className="w-full rounded-lg shadow border border-gray-300 p-2 justify-start mt-2 gap-2 inline-flex">
                                        <div className="inline-flex justify-start items-center gap-1">
                                            <div className="justify-start items-start flex">
                                                <div className="w-5 h-5 rounded-lg justify-center items-center flex">
                                                    <img className="w-5 h-5" src="/icons/icon.png" alt="icon"/>
                                                </div>
                                            </div>
                                            <div className="text-slate-700 text-sm font-medium leading-tight">Best Karuna Rating</div>
                                        </div>
                                        <div className="grow shrink h-2 mt-1.5 relative rounded-lg">
                                            <div className="w-[100%] h-2 left-0 top-0 absolute bg-gray-200 rounded-full"/>
                                            <div className={classNames("h-2 left-0 top-0 absolute rounded-full", scoreBarColor(routineBadgeData[selectedWeek].rating.score, false))} style={{ width: (Math.round((routineBadgeData[selectedWeek].rating.score / 5000) * 100)) + "%" }}/>
                                        </div>
                                        <div className="inline-flex items-center gap-1 right-0">
                                            <div className="text-right text-gray-700 text-wrap text-sm font-xs leading-tight">{routineBadgeData[selectedWeek].rating.score}</div>
                                            <div className="mix-blend-multiply justify-start items-start flex">
                                                <div className={`px-2 py-0.5 rounded-2xl justify-center items-center flex ${scoreBadgeBackgroundColor(routineBadgeData[selectedWeek].rating.score, false)}`}>
                                                    <div className={`text-center text-xs font-medium leading-[18px] text-nowrap ${scoreTextColor(routineBadgeData[selectedWeek].rating.score, false)}`}>
                                                        {scoreEncouragement(routineBadgeData[selectedWeek].rating.score, false)}
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                )
                                : (
                                    <div className="w-full rounded-lg shadow border p-2 border-gray-300 mt-2 justify-start items-center gap-2 inline-flex">
                                        <div className="inline-flex justify-start items-center gap-1">
                                            <div className="justify-start items-start flex">
                                                <div className="w-5 h-5 rounded-lg justify-center items-center flex">
                                                    <img className="w-5 h-5" src="/icons/icon.png" alt="icon"/>
                                                </div>
                                            </div>
                                            <div className="text-slate-700 text-sm font-medium leading-tight">Best Karuna Rating</div>
                                        </div>
                                        <div className="grow shrink h-2 relative rounded-lg">
                                            <div className="w-[100%] h-2 left-0 top-0 absolute bg-gray-200 rounded-full"/>
                                        </div>
                                        <div className="inline-flex items-center gap-1 right-0">
                                            <div className="text-right text-gray-700 text-wrap text-sm font-xs leading-tight">0</div>
                                            <div className="mix-blend-multiply justify-start items-start flex">
                                                <div className="px-2 py-0.5 bg-gray-50 rounded-2xl justify-center items-center flex">
                                                    <div className="text-center text-nowrap text-gray-700 text-xs font-medium leading-[18px]">None</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                        }
                        {
                            activityStats &&
                            personalRoutine &&
                            (state === ViewState.Active || routineBadgeData?.[selectedWeek]?.scores?.length !== 0)
                                ? (
                                    <div className="w-full h-[100%] max-[1800px]:h-[100%] grid min-[1200px]:grid-rows-2 grid-cols-2 max-[1200px]:grid-cols-1 gap-4 my-2">
                                        {
                                            [0, 2, 4, 6].map(
                                                (index: number) => (
                                                    <div key={index} className="justify-center items-center inline-flex max-[1200px]:w-full min-w-0 min-h-0 relative rounded-[10px] shadow border border-gray-200">
                                                        <AssignmentEditor
                                                            activities={activityStats}
                                                            completedRoutineData={routineBadgeData}
                                                            currentAssignments={personalRoutine.assignments}
                                                            defaultAssignment={personalRoutine.assignments[index]}
                                                            defaultDuration={personalRoutine?.assignments?.[index]?.Id ? hasDuration(personalRoutine.assignments[index]) : null}
                                                            index={index}
                                                            isCalibrationActivity={index === 0}
                                                            isRoutineActivity={true}
                                                            originalAssignment={constRoutine.assignments[index]}
                                                            originalDuration={constRoutine?.assignments?.[index]?.Id ? hasDuration(constRoutine.assignments[index]) : null}
                                                            refreshTrigger={refreshTrigger}
                                                            state={state}
                                                            vetKitVersion={vetKitVersion}
                                                            week={selectedWeek}
                                                            // Callbacks
                                                            refreshParentCallback={refreshCallback}
                                                            setActivityCallback={setActivityCallback}
                                                            setDurationCallback={setDurationCallback}
                                                            setOptionsCallback={setOptionsCallback}
                                                        />
                                                    </div>
                                                )
                                            )
                                        }
                                    </div>
                                )
                                : (
                                    <div className="w-full text-wrap pt-2 text-gray-900 text-sm font-normal leading-tight">
                                        No recorded routine data for this week.
                                    </div>
                                )
                        }
                    </div>
                )
            }
        </div>
    );
};

export default PersonalRoutineEditor;
